import React, { useState, useRef, useEffect } from "react";
import { MapPin, Navigation } from "lucide-react";

/**
 * Clickable address that opens a small popover with navigation app choices:
 * Google Maps, Waze, Apple Plans (iOS), and a generic "geo:" deep-link
 * that lets the OS offer the user's installed nav apps (Mappy, Here, etc.).
 *
 * Props:
 *  - address: string (required)
 *  - lat, lng: optional numbers — used for higher-precision deep-links
 *  - className: extra wrapper classes
 *  - inline: render inline (no MapPin icon prefix)
 */
export default function AddressActions({ address, lat, lng, className = "", inline = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const q = encodeURIComponent(address || "");
  const hasCoords = typeof lat === "number" && typeof lng === "number" && !Number.isNaN(lat) && !Number.isNaN(lng);
  const ll = hasCoords ? `${lat},${lng}` : "";

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!address) return null;

  const links = [
    {
      key: "gmaps",
      label: "Google Maps",
      // Universal Maps URL — works on iOS/Android/desktop
      href: hasCoords
        ? `https://www.google.com/maps/dir/?api=1&destination=${ll}`
        : `https://www.google.com/maps/search/?api=1&query=${q}`,
    },
    {
      key: "waze",
      label: "Waze",
      href: hasCoords ? `https://waze.com/ul?ll=${ll}&navigate=yes` : `https://waze.com/ul?q=${q}&navigate=yes`,
    },
    {
      key: "apple",
      label: "Plans (Apple)",
      href: hasCoords ? `https://maps.apple.com/?daddr=${ll}` : `https://maps.apple.com/?q=${q}`,
    },
    {
      key: "geo",
      label: "Autre application…",
      // geo:lat,lng?q=address opens the native picker on Android (Mappy, HERE, OsmAnd…).
      // Desktop browsers fall back to the default handler.
      href: hasCoords ? `geo:${ll}?q=${ll}(${q})` : `geo:0,0?q=${q}`,
    },
  ];

  return (
    <span ref={ref} className={`relative inline-flex items-center ${className}`}>
      {!inline && <MapPin className="w-4 h-4 mr-1 text-sky-700" aria-hidden />}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-700 hover:decoration-solid focus:outline-none"
        title="Ouvrir dans une application de navigation"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="address-actions-trigger"
      >
        {address}
      </button>
      {open && (
        <span
          role="menu"
          className="absolute left-0 top-full mt-1 z-[1600] min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-xl p-1 text-sm"
          data-testid="address-actions-menu"
        >
          <span className="block px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
            <Navigation className="w-3 h-3" />Itinéraire vers…
          </span>
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="block px-3 py-2 rounded-lg hover:bg-sky-50 hover:text-sky-700 transition-colors"
              data-testid={`address-actions-${l.key}`}
            >
              {l.label}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}
